import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CategoryFilter, { type CategoryFilterState } from '@/pages/category/components/CategoryFilter';
import CourseCard from '@/components/base/CourseCard';
import { getAllCategories, getAllCourses, getCategoryById } from '@/utils/course';

const sortOptions = [
  { key: 'default', label: '默认推荐' },
  { key: 'latest', label: '最新发布' },
  { key: 'students', label: '学习人数' },
  { key: 'rating', label: '评价最高' },
];

function parseDurationHours(duration: string): number {
  const match = duration.match(/(\d+)/);
  return match ? Number(match[1]) : 0;
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(6);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const cat = searchParams.get('cat') ?? '';
  const state: CategoryFilterState = {
    sub: searchParams.get('sub') ?? '',
    level: searchParams.get('level') ?? '',
    duration: searchParams.get('duration') ?? '',
    price: searchParams.get('price') ?? '',
  };
  const sort = searchParams.get('sort') ?? 'default';

  const activeCategory = cat ? getCategoryById(cat) : undefined;
  const allCourses = getAllCourses();

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
    setVisibleCount(6);
  };

  const handleSelect = (group: keyof CategoryFilterState, value: string) => {
    updateParams({ [group]: value });
  };

  const handleReset = () => {
    const next = new URLSearchParams();
    if (cat) next.set('cat', cat);
    if (sort !== 'default') next.set('sort', sort);
    setSearchParams(next);
    setVisibleCount(6);
  };

  const filtered = useMemo(() => {
    let list = allCourses;
    if (cat) list = list.filter((c) => c.category === cat);
    if (state.sub) list = list.filter((c) => c.subcategory === state.sub);
    if (state.level) list = list.filter((c) => c.level === state.level);
    if (state.price) list = list.filter((c) => c.priceType === state.price);
    if (state.duration) {
      list = list.filter((c) => {
        const hours = parseDurationHours(c.duration);
        if (state.duration === '10小时以内') return hours <= 10;
        if (state.duration === '10-20小时') return hours > 10 && hours <= 20;
        return hours > 20;
      });
    }
    const sorted = [...list];
    if (sort === 'latest') sorted.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
    else if (sort === 'students') sorted.sort((a, b) => b.students - a.students);
    else if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.views - a.views);
    return sorted;
  }, [allCourses, cat, state.sub, state.level, state.price, state.duration, sort]);

  const visible = filtered.slice(0, visibleCount);
  const categories = getAllCategories();
  const subcategories = activeCategory?.subcategories ?? [];

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
        <span>/</span>
        <Link to="/category" className="hover:text-primary-600 cursor-pointer">课程分类</Link>
        {activeCategory && (
          <>
            <span>/</span>
            <span className="text-foreground-800">{activeCategory.name}</span>
          </>
        )}
      </nav>

      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mb-2">
          {activeCategory ? activeCategory.name : '全部课程'}
        </h1>
        <p className="text-sm text-foreground-600 max-w-3xl">
          {activeCategory
            ? `${activeCategory.desc} 共 ${filtered.length} 门课程，支持按子类别、难度、时长与费用类型筛选。`
            : `浏览平台全部课程，共 ${filtered.length} 门，支持多维度筛选与排序，快速定位你想学的内容。`}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          type="button"
          onClick={() => updateParams({ cat: '', sub: '' })}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
            !cat ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300'
          }`}
        >
          全部
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => updateParams({ cat: c.id, sub: '' })}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              cat === c.id ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300'
            }`}
          >
            <span className="w-4 h-4 flex items-center justify-center text-base">
              <i className={c.icon}></i>
            </span>
            {c.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 md:gap-8 items-start">
        <aside className="hidden lg:block sticky top-24">
          <CategoryFilter
            subcategories={subcategories}
            state={state}
            onSelect={handleSelect}
            onReset={handleReset}
          />
        </aside>

        <div>
          <div className="flex items-center justify-between gap-3 mb-5">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-filter-3-line"></i>
              </span>
              筛选
            </button>
            <span className="hidden lg:block text-sm text-foreground-500">
              共 <strong className="text-foreground-900">{filtered.length}</strong> 门课程
            </span>
            <div className="flex items-center gap-1.5 overflow-x-auto">
              <span className="text-sm text-foreground-500 shrink-0 hidden sm:block">排序：</span>
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => updateParams({ sort: opt.key })}
                  className={`px-3 py-1.5 rounded-md text-xs md:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sort === opt.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {visible.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
              {visibleCount < filtered.length && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + 6)}
                    className="inline-flex items-center gap-2 h-11 px-8 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    加载更多
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-20 rounded-lg border border-dashed border-background-300 bg-background-50">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-4">
                <i className="ri-search-eye-line"></i>
              </span>
              <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">
                暂无课程，尝试更改筛选条件
              </h3>
              <p className="text-sm text-foreground-500 mb-5">换一组筛选条件，或许会有更多发现。</p>
              <button
                type="button"
                onClick={handleReset}
                className="h-10 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-background-50 overflow-y-auto p-4 animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading font-bold text-lg text-foreground-950">筛选条件</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 text-2xl cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <CategoryFilter
              subcategories={subcategories}
              state={state}
              onSelect={handleSelect}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="w-full h-11 mt-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              查看 {filtered.length} 门课程
            </button>
          </div>
        </div>
      )}
    </div>
  );
}